// Every backend path the admin app calls, in one place, grouped by what it is
// for rather than by which server answers it.
//
// Anything under /api/v2 is the Python service. Anything that is not is still
// served by one of the legacy services.
//
// These are backend contracts: do not "tidy" a path, a casing oddity or a
// trailing slash without checking the server first.

export const endpoints = {
  // --- People and permissions ---------------------------------------------
  users: {
    list: () => "/api/v2/auth/users/",
    create: () => "/api/v2/auth/users/",
    byId: (userId: string) => `/api/v2/auth/users/${userId}/`,
    update: (userId: string) => `/api/v2/auth/users/${userId}/`,
    remove: (userId: string) => `/api/v2/auth/users/${userId}/`,
    /** Body {enabled} to set it, or omit the body to flip it. */
    setEnabled: (userId: string) => `/api/v2/auth/users/${userId}/enabled/`,
    /** ?status=APPROVED|REJECTED, body {comment}. */
    review: (userId: string) => `/api/v2/auth/users/${userId}/review/`,
    download: () => "/api/v2/auth/users/export/",
    profileImage: (userId: string) => `/api/v2/auth/users/${userId}/profile-image/`,
    /** What they entered on the registration form, with every option resolved to its label. */
    registrantProfile: (userId: string) => `/api/v2/auth/users/${userId}/profile/`,
    profileChangeRequests: (email: string) => `/api/users/profileChangeRequests/${email}`,
  },
  roles: {
    list: () => "/api/v2/auth/roles/",
    create: () => "/api/v2/auth/roles/",
    update: (roleId: string) => `/api/v2/auth/roles/${roleId}/`,
    remove: (roleId: string) => `/api/v2/auth/roles/${roleId}/`,
  },
  authorities: {
    list: () => "/api/v2/auth/authorities/",
  },
  account: {
    /** The signed-in person's own record. Self-service: no admin permission. */
    me: (userId: string) => `/api/v2/auth/${userId}`,
    changePassword: () => "/api/auth/oauth/change-password",
    /** Sends the reset OTP. */
    forgotPasswordOtp: () => "/api/auth/otps/resetPassword",
    /** Redeems the reset OTP for a new password. */
    resetPassword: () => "/api/auth/oauth/reset-password",
    emailChangeOtp: () => "/api/auth/otps/emailChange",
    confirmEmailChange: () => "/api/auth/oauth/users/emailConfirmations",
  },

  // --- Surveys, registration and media ------------------------------------
  surveys: {
    list: () => "/api/v2/surveys/",
    create: () => "/api/v2/surveys/create/",
    byId: (surveyId: string) => `/api/v2/surveys/${surveyId}/`,
    update: (surveyId: string) => `/api/v2/surveys/${surveyId}/`,
    remove: (surveyId: string) => `/api/v2/surveys/${surveyId}/`,
    copy: (surveyId: string) => `/api/v2/surveys/${surveyId}/copies/`,
    publish: (surveyId: string) => `/api/v2/surveys/${surveyId}/publish/`,
    cancel: (surveyId: string) => `/api/v2/surveys/${surveyId}/cancelled/`,
    download: (surveyId: string) => `/api/v2/surveys/${surveyId}/downloads/`,
    questions: (surveyId: string) => `/api/v2/surveys/${surveyId}/questions/`,
    question: (surveyId: string, questionId: string) =>
      `/api/v2/surveys/${surveyId}/questions/${questionId}/`,
    questionOrder: (surveyId: string) => `/api/v2/surveys/${surveyId}/question-order/`,
    targetOptions: (surveyId: string) => `/api/v2/surveys/${surveyId}/target-options/`,
    remind: (surveyId: string) => `/api/v2/surveys/${surveyId}/remind/`,
  },
  questionTypes: {
    list: () => "/api/v2/surveys/question-types/",
  },
  metas: {
    profileProperties: () => "/api/v2/surveys/metas/profile-properties/",
    profileProperty: (id: string) => `/api/metas/profileProperties/${id}`,
    profilePropertyOrder: (id: string) => `/api/metas/profileProperties/${id}/updateOrder`,
    allOptions: () => "/api/v2/surveys/metas/all-options/",
    states: () => "/api/v2/surveys/metas/states/",
    cities: () => "/api/v2/surveys/metas/cities/",
    configs: () => "/api/metas/configs",
    userTypes: () => "/api/metas/userTypes",
    /** The registration editor builds option paths at runtime. */
    dynamic: (endpoint: string) => `/api${endpoint}`,
    dynamicById: (endpoint: string, id: string) => `/api${endpoint}/${id}`,
  },
  uploads: {
    list: () => "/api/v2/uploads/",
    create: () => "/api/v2/uploads/",
    remove: (id: string) => `/api/v2/uploads/${id}/`,
  },

  // --- Notifications ------------------------------------------------------
  events: {
    templateKeys: (eventName: string) => `/api/events/${eventName}/templateKeys`,
  },

  // --- App content --------------------------------------------------------
  newsfeed: {
    /** No trailing slash -- Django routes this as `all`, not `all/`. */
    list: () => "/api/v2/newsfeed/all",
    create: () => "/api/v2/newsfeed/all/create/",
    update: (id: string) => `/api/v2/newsfeed/all/edit/${id}/`,
    remove: (id: string) => `/api/v2/newsfeed/all/${id}/`,
  },
  calendar: {
    // Django mounts these at BOTH /api/v2/ and /api/v2/events/. The recorded
    // contracts use the /events/ form; keep them matching.
    profiles: () => "/api/v2/events/google-profiles/",
    preview: (calendarId: string) => `/api/v2/events/google-calendar/${calendarId}/preview/`,
    update: (calendarId: string) => `/api/v2/events/google-calendar/${calendarId}/update/`,
  },
  clergy: {
    list: () => "/api/v2/clergy-directory/",
  },
  csrf: {
    set: () => "/api/v2/set-csrf-token/",
  },
} as const;
